/**
 * BM25L Reranker for hybrid semantic search
 *
 * BM25L is an improved variant of BM25 that provides better handling of
 * long documents and term frequency saturation. This implementation is
 * designed to rerank semantic search results from ChromaDB.
 *
 * The hybrid approach combines:
 * 1. Semantic similarity from ChromaDB embeddings (dense vectors)
 * 2. Lexical matching from BM25L (sparse, keyword-based)
 *
 * This addresses the weakness of pure semantic search which may miss
 * exact keyword matches that are important for user intent.
 */

export interface BM25LOptions {
	/** Term frequency saturation parameter (default: 1.5) */
	k1?: number;
	/** Length normalization parameter (default: 0.75) */
	b?: number;
	/** Lower-bound adjustment from BM25L paper (default: 0.5) */
	delta?: number;
}

export interface RerankedResult<T> {
	item: T;
	originalIndex: number;
	semanticScore: number;
	bm25Score: number;
	hybridScore: number;
}

export interface HybridSearchOptions extends BM25LOptions {
	/** Weight for semantic score (0-1, default: 0.7) */
	semanticWeight?: number;
	/** Weight for BM25 score (0-1, default: 0.3) */
	bm25Weight?: number;
	/** Minimum hybrid score threshold (0-1, default: 0) */
	minScore?: number;
}

/**
 * BM25L implementation for lexical scoring
 */
export class BM25L {
	private k1: number;
	private b: number;
	private delta: number;
	private documents: string[][];
	private docLengths: number[];
	private avgDocLength: number;
	private termDocFreq: Record<string, number>;

	/**
	 * @param documents - Array of raw documents (strings)
	 * @param opts - Optional BM25L parameters
	 */
	constructor(documents: string[] = [], opts: BM25LOptions = {}) {
		if (!Array.isArray(documents)) {
			throw new Error('BM25L: documents must be an array of strings.');
		}

		this.k1 = typeof opts.k1 === 'number' ? opts.k1 : 1.5;
		this.b = typeof opts.b === 'number' ? opts.b : 0.75;
		this.delta = typeof opts.delta === 'number' ? opts.delta : 0.5;

		// Tokenize documents safely
		this.documents = documents.map(d => (typeof d === 'string' ? this.tokenize(d) : []));

		// Precompute stats
		this.docLengths = this.documents.map(doc => doc.length);
		this.avgDocLength =
			this.docLengths.reduce((a, b) => a + b, 0) / (this.docLengths.length || 1);

		// Compute document frequency (df)
		this.termDocFreq = {};
		this.documents.forEach(doc => {
			const seen = new Set<string>();
			doc.forEach(term => {
				if (!seen.has(term)) {
					seen.add(term);
					this.termDocFreq[term] = (this.termDocFreq[term] || 0) + 1;
				}
			});
		});
	}

	/**
	 * Tokenize text into lowercase alphanumeric tokens
	 */
	tokenize(text: string): string[] {
		if (typeof text !== 'string') return [];
		return text
			.toLowerCase()
			.replace(/[^a-z0-9\s]/g, ' ')
			.split(/\s+/)
			.filter(Boolean);
	}

	/**
	 * Compute IDF (Inverse Document Frequency) with smoothing
	 */
	private idf(term: string): number {
		const df = this.termDocFreq[term] || 0;
		const N = this.documents.length || 1;
		return Math.log(1 + (N - df + 0.5) / (df + 0.5));
	}

	/**
	 * Compute BM25L score for a single document
	 */
	score(query: string, docIndex: number): number {
		if (typeof query !== 'string') return 0;
		if (docIndex < 0 || docIndex >= this.documents.length) return 0;

		const tokens = this.tokenize(query);
		if (tokens.length === 0) return 0;

		const doc = this.documents[docIndex];
		const docLength = this.docLengths[docIndex] || 1;

		// Term frequencies
		const freq: Record<string, number> = {};
		for (const t of doc) {
			freq[t] = (freq[t] || 0) + 1;
		}

		let sum = 0;

		for (const term of tokens) {
			const tf = freq[term] || 0;
			if (tf === 0) continue;

			const idfVal = this.idf(term);

			// BM25L adjusted TF
			let tfL = tf - this.b * (docLength / this.avgDocLength) + this.delta;
			if (tfL < 0) tfL = 0;

			sum += idfVal * (tfL / (this.k1 + tfL));
		}

		return sum;
	}

	/**
	 * Search and rank all documents
	 */
	search(query: string): Array<{ index: number; score: number }> {
		return this.documents
			.map((_, i) => ({
				index: i,
				score: this.score(query, i)
			}))
			.sort((a, b) => b.score - a.score);
	}
}

/**
 * Normalize scores to 0-1 range using min-max normalization
 */
function normalizeScores(scores: number[]): number[] {
	if (scores.length === 0) return [];

	const min = Math.min(...scores);
	const max = Math.max(...scores);

	// If all scores are the same, return array of 1s (or 0s if all are 0)
	if (max === min) {
		return scores.map(() => (max === 0 ? 0 : 1));
	}

	return scores.map(score => (score - min) / (max - min));
}

/**
 * Hybrid reranker that combines semantic and BM25L scores
 *
 * @param query - The search query
 * @param items - Array of items to rerank
 * @param getDocument - Function to extract document text from an item
 * @param getSemanticScore - Function to extract semantic similarity score from an item
 * @param options - Hybrid search options
 * @returns Reranked items with hybrid scores
 */
export function hybridRerank<T>(
	query: string,
	items: T[],
	getDocument: (item: T) => string,
	getSemanticScore: (item: T) => number,
	options: HybridSearchOptions = {}
): RerankedResult<T>[] {
	const {
		semanticWeight = 0.7,
		bm25Weight = 0.3,
		minScore = 0,
		k1 = 1.5,
		b = 0.75,
		delta = 0.5
	} = options;

	if (items.length === 0) return [];

	// Extract documents and semantic scores
	const documents = items.map(getDocument);
	const semanticScores = items.map(getSemanticScore);

	// Create BM25L index and compute scores
	const bm25 = new BM25L(documents, { k1, b, delta });
	const bm25Scores = items.map((_, i) => bm25.score(query, i));

	// Normalize both score sets to 0-1 range
	const normalizedSemantic = normalizeScores(semanticScores);
	const normalizedBM25 = normalizeScores(bm25Scores);

	// Compute hybrid scores and create result objects
	const results: RerankedResult<T>[] = items.map((item, i) => {
		const hybridScore =
			semanticWeight * normalizedSemantic[i] + bm25Weight * normalizedBM25[i];

		return {
			item,
			originalIndex: i,
			semanticScore: semanticScores[i],
			bm25Score: bm25Scores[i],
			hybridScore
		};
	});

	// Sort by hybrid score (descending) and filter by minimum score
	return results
		.filter(r => r.hybridScore >= minScore)
		.sort((a, b) => b.hybridScore - a.hybridScore);
}

/**
 * Simple reranking function for ChromaDB results
 *
 * This is a convenience wrapper for reranking ChromaDB query results
 * that follow the standard { ids, documents, metadatas, distances } format.
 *
 * @param query - The search query
 * @param chromaResults - ChromaDB query results
 * @param options - Hybrid search options
 * @returns Reranked results with hybrid scores
 */
export function rerankChromaResults(
	query: string,
	chromaResults: {
		ids: string[][];
		documents: (string | null)[][];
		metadatas: Record<string, any>[][];
		distances: number[][];
	},
	options: HybridSearchOptions = {}
): Array<{
	id: string;
	document: string | null;
	metadata: Record<string, any>;
	distance: number;
	semanticScore: number;
	bm25Score: number;
	hybridScore: number;
}> {
	// Flatten the nested arrays (ChromaDB returns nested arrays for batch queries)
	const ids = chromaResults.ids[0] || [];
	const documents = chromaResults.documents[0] || [];
	const metadatas = chromaResults.metadatas[0] || [];
	const distances = chromaResults.distances[0] || [];

	if (ids.length === 0) return [];

	// Create items array
	const items = ids.map((id, i) => ({
		id,
		document: documents[i],
		metadata: metadatas[i],
		distance: distances[i]
	}));

	// Rerank using hybrid approach
	const reranked = hybridRerank(
		query,
		items,
		item => item.document || '',
		// Convert L2 distance to similarity score
		item => 1 / (1 + item.distance),
		options
	);

	// Return flattened results
	return reranked.map(r => ({
		id: r.item.id,
		document: r.item.document,
		metadata: r.item.metadata,
		distance: r.item.distance,
		semanticScore: r.semanticScore,
		bm25Score: r.bm25Score,
		hybridScore: r.hybridScore
	}));
}

/**
 * Rerank conversation search results specifically
 *
 * This function is designed to work with the conversation-history.search collection
 * where we need to fetch more results initially and then rerank them.
 *
 * @param query - The user's search query
 * @param results - Array of conversation search results from ChromaDB
 * @param options - Hybrid search options
 * @returns Reranked results sorted by hybrid score
 */
export function rerankConversationResults<T extends { userPrompt?: string; similarity?: number }>(
	query: string,
	results: T[],
	options: HybridSearchOptions = {}
): Array<T & { hybridScore: number; bm25Score: number }> {
	if (results.length === 0) return [];

	const reranked = hybridRerank(
		query,
		results,
		item => item.userPrompt || '',
		item => item.similarity || 0,
		options
	);

	return reranked.map(r => ({
		...r.item,
		hybridScore: r.hybridScore,
		bm25Score: r.bm25Score
	}));
}

export default {
	BM25L,
	hybridRerank,
	rerankChromaResults,
	rerankConversationResults
};
